// LT-352 (LT-325 C5): goal text + KPI editor / viewer.
//
// Editor used by CampaignFormPage; viewer used by CampaignDetailPage.
// LT-564: KPI name is operator-defined free text. The four predefined
// names (qualification_rate, meetings_booked, pipeline_dollars,
// time_to_qualify) are offered as datalist *suggestions* and auto-fill
// their unit, but operators may type any name. Names that match a
// predefined metric are auto-tracked; everything else persists and shows
// a "Not tracked" pill on the viewer.
import React from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { Button, Card, IconButton, Input, Pill, Select, Textarea } from '../../ui';

export type CampaignKpiUnit = '%' | 'count' | '$' | 'days';

export interface CampaignKpi {
  name: string;
  target_value: number;
  unit: CampaignKpiUnit | string;
  // LT-647: optional operator definition. Injected into the post-call
  // analyser prompt so a bespoke KPI is judged against this meaning rather
  // than the name alone — important when KPI names are ambiguous.
  description?: string;
}

export interface CampaignKpiProgress extends CampaignKpi {
  current_value: number;
  computed: boolean;
  computed_at?: string;
}

export const PREDEFINED_KPIS: Record<string, CampaignKpiUnit> = {
  qualification_rate: '%',
  meetings_booked: 'count',
  pipeline_dollars: '$',
  time_to_qualify: 'days',
};

export const KPI_LABELS: Record<string, string> = {
  qualification_rate: 'Qualification rate',
  meetings_booked: 'Meetings booked',
  pipeline_dollars: 'Pipeline dollars',
  time_to_qualify: 'Time to qualify',
};

export const KPI_UNIT_OPTIONS: CampaignKpiUnit[] = ['%', 'count', '$', 'days'];

export const MAX_KPIS_PER_CAMPAIGN = 8;

/** Normalise raw JSONB array into the typed shape; drop malformed entries. */
export function normalizeKpis(raw: unknown): CampaignKpi[] {
  if (!Array.isArray(raw)) return [];
  const out: CampaignKpi[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const obj = entry as Record<string, unknown>;
    const name = typeof obj.name === 'string' ? obj.name.trim() : '';
    if (!name) continue;
    const target = Number(obj.target_value);
    if (!Number.isFinite(target) || target <= 0) continue;
    const unit =
      typeof obj.unit === 'string' && obj.unit.length > 0
        ? (obj.unit as CampaignKpiUnit)
        : (PREDEFINED_KPIS[name] ?? '%');
    const description =
      typeof obj.description === 'string' && obj.description.trim()
        ? obj.description.trim()
        : undefined;
    out.push({ name, target_value: target, unit, ...(description ? { description } : {}) });
  }
  return out;
}

interface CampaignGoalsKpisEditorProps {
  goalText: string;
  kpis: CampaignKpi[];
  disabled?: boolean;
  onGoalTextChange: (next: string) => void;
  onKpisChange: (next: CampaignKpi[]) => void;
}

export const CampaignGoalsKpisEditor: React.FC<CampaignGoalsKpisEditorProps> = ({
  goalText, kpis, disabled, onGoalTextChange, onKpisChange,
}) => {
  const usedNames = new Set(kpis.map((k) => k.name));
  const availablePredefined = Object.keys(PREDEFINED_KPIS).filter(
    (name) => !usedNames.has(name),
  );

  const addKpi = () => {
    if (kpis.length >= MAX_KPIS_PER_CAMPAIGN) return;
    const name = availablePredefined[0] ?? 'qualification_rate';
    const unit = PREDEFINED_KPIS[name] ?? '%';
    onKpisChange([...kpis, { name, target_value: 0, unit }]);
  };

  const updateKpi = (index: number, patch: Partial<CampaignKpi>) => {
    onKpisChange(
      kpis.map((k, i) => {
        if (i !== index) return k;
        const next = { ...k, ...patch };
        // When name flips to a predefined entry, auto-fill the unit.
        if (patch.name && PREDEFINED_KPIS[patch.name]) {
          next.unit = PREDEFINED_KPIS[patch.name];
        }
        return next;
      }),
    );
  };

  const removeKpi = (index: number) => {
    onKpisChange(kpis.filter((_, i) => i !== index));
  };

  return (
    <Card>
      <h3 className="text-sm font-semibold text-slate-200 mb-1">Goals + KPIs</h3>
      <p className="text-xs text-slate-500 mb-3">
        Free-text goal + numeric targets. Live progress lands on the campaign
        detail page once the enrolment table is in place (LT-354).
      </p>

      <label className="block mb-4">
        <span className="text-xs text-slate-400">Goal</span>
        <Textarea
          value={goalText}
          onChange={(e) => onGoalTextChange(e.target.value)}
          disabled={disabled}
          rows={2}
          placeholder="e.g. Book 25 qualified meetings with VPs Sales by end of Q2."
        />
      </label>

      <div className="flex flex-col gap-2">
        <datalist id="kpi-name-suggestions">
          {Object.keys(PREDEFINED_KPIS).map((name) => (
            <option key={name} value={name}>{KPI_LABELS[name]}</option>
          ))}
        </datalist>
        {kpis.map((k, i) => {
          return (
            <div
              key={i}
              className="flex flex-col gap-2 px-3 py-2 rounded-ds-md border border-white/[0.08] bg-white/[0.02]"
            >
              <div className="flex items-end gap-2">
                <label className="flex-1 block">
                  <span className="text-[11px] text-slate-500 uppercase tracking-wider">Name</span>
                  <Input
                    value={k.name}
                    onChange={(e) => updateKpi(i, { name: e.target.value })}
                    disabled={disabled}
                    list="kpi-name-suggestions"
                    placeholder="e.g. meetings_booked or your own metric"
                  />
                </label>
                <label className="block w-32">
                  <span className="text-[11px] text-slate-500 uppercase tracking-wider">Target</span>
                  <Input
                    type="number"
                    min="0.01"
                    step="any"
                    value={k.target_value || ''}
                    onChange={(e) =>
                      updateKpi(i, { target_value: Number(e.target.value) })
                    }
                    disabled={disabled}
                  />
                </label>
                <label className="block w-24">
                  <span className="text-[11px] text-slate-500 uppercase tracking-wider">Unit</span>
                  <Select
                    value={k.unit}
                    onChange={(e) => updateKpi(i, { unit: e.target.value as CampaignKpiUnit })}
                    disabled={disabled}
                  >
                    {KPI_UNIT_OPTIONS.map((u) => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </Select>
                </label>
                <IconButton
                  aria-label={`Remove KPI ${i + 1}`}
                  disabled={disabled}
                  onClick={() => removeKpi(i)}
                >
                  <Trash2 size={14} />
                </IconButton>
              </div>
              <label className="block">
                <span className="text-[11px] text-slate-500 uppercase tracking-wider">
                  Definition <span className="normal-case text-slate-600">(optional — how the AI judges this KPI per call)</span>
                </span>
                <Input
                  value={k.description ?? ''}
                  onChange={(e) => updateKpi(i, { description: e.target.value })}
                  disabled={disabled}
                  placeholder="e.g. candidate explicitly agreed to a screening interview"
                />
              </label>
            </div>
          );
        })}

        <Button
          intent="secondary"
          size="sm"
          onClick={addKpi}
          disabled={disabled || kpis.length >= MAX_KPIS_PER_CAMPAIGN}
          className="self-start"
        >
          <Plus size={14} strokeWidth={2.5} />
          Add KPI
        </Button>
        {kpis.length >= MAX_KPIS_PER_CAMPAIGN ? (
          <p className="text-xs text-slate-500">
            Max {MAX_KPIS_PER_CAMPAIGN} KPIs per campaign.
          </p>
        ) : null}
      </div>
    </Card>
  );
};

interface CampaignGoalsKpisPanelProps {
  goalText: string | null;
  progress: CampaignKpiProgress[];
}

export const CampaignGoalsKpisPanel: React.FC<CampaignGoalsKpisPanelProps> = ({
  goalText, progress,
}) => {
  const empty = !goalText && progress.length === 0;

  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-200">Goals + KPIs</h3>
        {empty ? <Pill intent="neutral">Not set</Pill> : <Pill intent="info">Set</Pill>}
      </div>

      {empty ? (
        <p className="text-xs text-slate-500">
          Add a goal statement or KPIs from the campaign edit page to track progress.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {goalText ? (
            <div>
              <div className="text-xs text-slate-500">Goal</div>
              <p className="text-sm text-slate-200 whitespace-pre-wrap">{goalText}</p>
            </div>
          ) : null}

          {progress.length > 0 ? (
            <div className="flex flex-col gap-3">
              <div className="text-xs text-slate-500">KPIs</div>
              {progress.map((kpi) => (
                <KpiProgressRow key={kpi.name} kpi={kpi} />
              ))}
            </div>
          ) : null}
        </div>
      )}
    </Card>
  );
};

const KpiProgressRow: React.FC<{ kpi: CampaignKpiProgress }> = ({ kpi }) => {
  const label = KPI_LABELS[kpi.name] ?? kpi.name;
  const pct = kpi.target_value > 0
    ? Math.min(100, Math.round((kpi.current_value / kpi.target_value) * 100))
    : 0;
  const overTarget = kpi.current_value > kpi.target_value;

  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-slate-300">{label}</span>
        {kpi.computed ? (
          <span className="text-slate-400 font-mono">
            {formatValue(kpi.current_value, kpi.unit)} /{' '}
            {formatValue(kpi.target_value, kpi.unit)}
          </span>
        ) : (
          // LT-564: a non-computed KPI has no auto-tracking source (a
          // custom operator metric). Label it "Not tracked" rather than
          // "No data yet", which falsely implied data was still coming.
          <Pill intent="neutral">Not tracked</Pill>
        )}
      </div>
      {kpi.computed ? (
        <div className="h-1.5 rounded-full bg-white/[0.08] overflow-hidden">
          <div
            className={`h-full ${overTarget ? 'bg-ok' : 'bg-accent'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      ) : (
        <div className="h-1.5 rounded-full bg-white/[0.04]" />
      )}
    </div>
  );
};

function formatValue(value: number, unit: string): string {
  if (unit === '$') {
    return value >= 1000
      ? `$${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}k`
      : `$${value.toFixed(0)}`;
  }
  if (unit === '%') return `${value.toFixed(1)}%`;
  if (unit === 'days') return `${value.toFixed(1)}d`;
  return value.toFixed(0);
}
